import { useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  BookOpen,
  Key,
  Package,
  Terminal,
  Code,
  Layers,
  FileCode,
  Archive,
  History,
  Menu,
  X,
  ChevronRight,
  Home,
  ExternalLink,
} from 'lucide-react';

const navigation = [
  { name: 'Getting Started', href: '/developers/getting-started', icon: BookOpen },
  { name: 'Authentication', href: '/developers/authentication', icon: Key },
  { name: 'SDK Quickstart', href: '/developers/sdk-quickstart', icon: Package },
  { name: 'CLI Reference', href: '/developers/cli-reference', icon: Terminal },
  { name: 'API Reference', href: '/developers/api-reference', icon: Code },
  { name: 'Architecture', href: '/developers/architecture', icon: Layers },
  { name: 'Policy Authoring', href: '/developers/policy-authoring', icon: FileCode },
  { name: 'Bundle Compilation', href: '/developers/bundle-compilation', icon: Archive },
  { name: 'Changelog', href: '/developers/changelog', icon: History },
];

export function DevLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();

  const currentPage = navigation.find((item) => item.href === location.pathname);
  const sidebarClass = `fixed top-0 left-0 z-50 h-full w-72 bg-white border-r border-slate-200 transform transition-transform duration-200 ease-in-out lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-slate-900/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside className={sidebarClass}>
        <div className="flex items-center justify-between h-16 px-6 border-b border-slate-200">
          <NavLink to="/" className="flex items-center gap-2 text-slate-900 font-semibold">
            <Home className="w-5 h-5" />
            <span>Opair Platform</span>
          </NavLink>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-1.5 rounded-md hover:bg-slate-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="p-4 space-y-1 overflow-y-auto" style={{ height: 'calc(100% - 4rem)' }}>
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-3 py-2">
            Documentation
          </div>
          {navigation.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.name}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive ? 'bg-indigo-50 text-indigo-700' : 'text-slate-700 hover:bg-slate-100'
                  }`
                }
              >
                <Icon className="w-4 h-4 flex-shrink-0" />
                {item.name}
              </NavLink>
            );
          })}

          <div className="pt-4 mt-4 border-t border-slate-200">
            <a
              href="/api/docs"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-100 transition-colors"
            >
              <ExternalLink className="w-4 h-4 flex-shrink-0" />
              Swagger UI
            </a>
          </div>
        </nav>
      </aside>

      <div className="lg:pl-72">
        <header className="sticky top-0 z-30 flex items-center h-16 px-4 bg-white border-b border-slate-200 lg:hidden">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 -ml-2 rounded-md hover:bg-slate-100"
          >
            <Menu className="w-5 h-5" />
          </button>
          {currentPage && (
            <div className="flex items-center gap-2 ml-4 text-sm">
              <span className="text-slate-500">Docs</span>
              <ChevronRight className="w-4 h-4 text-slate-400" />
              <span className="font-medium text-slate-900">{currentPage.name}</span>
            </div>
          )}
        </header>

        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
